/**
 * Animals you have been.
 *
 * Kate's economy deliberately resets — 50 coupons buys one thing, and dying costs you
 * everything. So progression cannot come from keeping items, and whether you keep bought
 * morphs is an open question for her that I am not going to answer by myself.
 *
 * This adds accumulation without touching her rules: a record of every animal you have
 * ever been, kept across sessions. Nothing is spent, nothing is lost on death, and there
 * is a set to complete — the same pull the squishy cards have.
 */
import { SPECIES_ORDER, type SpeciesId } from './species'

const STORAGE_KEY = 'animal-attack:discovered'

export class Discovered {
  private readonly seen = new Set<SpeciesId>()

  constructor() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) for (const id of JSON.parse(raw) as SpeciesId[]) this.seen.add(id)
    } catch {
      // No history; start empty.
    }
  }

  get count(): number {
    return this.seen.size
  }

  get total(): number {
    return SPECIES_ORDER.length
  }

  has(id: SpeciesId): boolean {
    return this.seen.has(id)
  }

  /** Record having been this animal. Returns true the first time only. */
  record(id: SpeciesId): boolean {
    if (this.seen.has(id)) return false
    this.seen.add(id)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...this.seen]))
    } catch {
      // Private browsing; it still counts for this session.
    }
    return true
  }

  get remaining(): SpeciesId[] {
    return SPECIES_ORDER.filter((id) => !this.seen.has(id))
  }
}
